<template>
    <div class="page">
        <div class="page-header">
            <h1>角色管理</h1>
            <p>管理系統中的角色和相關權限設定</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 工具欄 -->
            <div class="toolbar">
                <div class="filters">
                    <a-space>
                        <a-input
                            v-model:value="searchText"
                            placeholder="角色名稱"
                            size="middle"
                            class="filter-input"
                        />
                        <a-button
                            size="middle"
                            class="unified-search-btn"
                            @click="handleSearch"
                        >
                            查詢
                        </a-button>
                        <a-button
                            size="middle"
                            class="unified-reset-btn"
                            @click="resetFilters"
                        >
                            重置
                        </a-button>
                    </a-space>
                </div>
                <a-button
                    v-if="canCreate"
                    type="primary"
                    class="unified-add-btn"
                    @click="openCreateRole"
                >
                    新增角色
                </a-button>
            </div>

            <!-- 角色列表表格 -->
            <DataTable
                title=""
                :columns="columns"
                :data-source="roles"
                :pagination="pagination"
                :show-index="true"
                :scroll="{ x: '100%' }"
                @refresh="loadRoles"
                @change="handleTableChange"
                class="role-table"
            >
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'name'">
                        <span class="role-name">{{ record.name }}</span>
                    </template>
                    <template v-else-if="column.key === 'users_count'">
                        <span
                            v-if="(record.users_count ?? 0) === 0"
                            class="users-count"
                        >
                            {{ record.users_count ?? 0 }}
                        </span>
                        <span
                            v-else
                            class="users-count clickable"
                            @click="goToAdminList(record)"
                        >
                            {{ record.users_count }}
                        </span>
                    </template>
                    <template v-else-if="column.key === 'permissions'">
                        <div class="permissions-cell">
                            <a-tag
                                v-for="(
                                    permission, index
                                ) in getDisplayPermissions(record.permissions)"
                                :key="permission.id"
                                color="blue"
                            >
                                {{ permission.name }}
                            </a-tag>
                            <span
                                v-if="
                                    record.permissions &&
                                    record.permissions.length > 3
                                "
                                class="more-permissions clickable"
                                @click="viewPermissions(record)"
                            >
                                +{{ record.permissions.length - 3 }}
                            </span>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'action'">
                        <a-space class="action-buttons">
                            <a-button
                                size="small"
                                class="action-btn-view"
                                @click="viewPermissions(record)"
                            >
                                檢視權限
                            </a-button>
                            <a-button
                                v-if="canEdit"
                                size="small"
                                class="action-btn-edit"
                                @click="openEditPermissions(record)"
                            >
                                編輯
                            </a-button>
                            <a-button
                                v-if="canDelete"
                                size="small"
                                class="action-btn-delete"
                                danger
                                :disabled="(record.users_count ?? 0) > 0"
                                @click="openDeleteRole(record)"
                            >
                                刪除
                            </a-button>
                        </a-space>
                    </template>
                </template>
            </DataTable>
        </div>

        <!-- 權限檢視 Modal -->
        <DetailModal
            v-model:open="permissionModalVisible"
            title="角色權限"
            width="960px"
            :data="currentRole"
            :basic-info="currentRoleInfo"
            :permissions="currentRolePermissions"
            :show-permissions="true"
            @cancel="permissionModalVisible = false"
        />

        <!-- 角色詳情 Modal -->
        <DetailModal
            v-model:open="detailModalVisible"
            title="角色詳情"
            width="960px"
            :data="roleDetail"
            :basic-info="roleDetailInfo"
            :permissions="roleDetail?.permissions"
            @cancel="detailModalVisible = false"
        />

        <!-- 編輯權限 Modal -->
        <EditModal
            v-model:open="editPermissionsModalVisible"
            title="編輯角色權限"
            width="960px"
            :confirm-loading="editPermissionsLoading"
            @ok="submitEditPermissions"
            @cancel="cancelEditPermissions"
        >
            <template #fields>
                <a-form-item label="角色名稱">
                    <a-input
                        v-model:value="editRoleName"
                        placeholder="請輸入角色名稱"
                    />
                </a-form-item>
                <a-form-item
                    label="權限清單"
                    :label-col="{ span: 24 }"
                    :wrapper-col="{ span: 24 }"
                >
                    <div class="perm-panel">
                        <div class="perm-tree-toolbar">
                            <a-space wrap size="small">
                                <a-switch
                                    v-model:checked="expandedAll"
                                    size="small"
                                    :checked-children="'展開'"
                                    :un-checked-children="'收合'"
                                />
                                <a-button
                                    size="small"
                                    @click="selectAllPermissions"
                                >
                                    全選
                                </a-button>
                                <a-button
                                    size="small"
                                    @click="clearAllPermissions"
                                >
                                    清除
                                </a-button>
                                <a-input-search
                                    v-model:value="permSearch"
                                    placeholder="搜尋權限名稱或代碼"
                                    allow-clear
                                    class="perm-search"
                                />
                            </a-space>
                            <span class="perm-summary">
                                已選 {{ selectedPermissionCount }} /
                                {{ totalPermissionCount }}
                            </span>
                        </div>
                        <div
                            v-if="permissionTreeData.length"
                            class="perm-scroll"
                        >
                            <a-collapse
                                v-model:activeKey="activeModuleKeys"
                                :bordered="false"
                                :destroyInactivePanel="true"
                                class="perm-collapse"
                            >
                                <a-collapse-panel
                                    v-for="root in filteredTreeData"
                                    :key="root.key"
                                    class="perm-module-panel"
                                >
                                    <template #header>
                                        <div class="perm-module-header">
                                            <span class="perm-module-title">
                                                {{ root.title }}
                                            </span>
                                            <span
                                                class="perm-module-meta"
                                                @click.stop
                                            >
                                                <span
                                                    class="perm-module-count"
                                                    :class="{
                                                        'is-complete':
                                                            moduleSelectionStats[
                                                                root.key
                                                            ]?.complete
                                                    }"
                                                >
                                                    {{
                                                        moduleSelectionStats[
                                                            root.key
                                                        ]?.selected ?? 0
                                                    }}/{{
                                                        moduleSelectionStats[
                                                            root.key
                                                        ]?.total ?? 0
                                                    }}
                                                </span>
                                                <a-button
                                                    type="link"
                                                    size="small"
                                                    @click.stop="
                                                        toggleModuleAll(
                                                            root,
                                                            true
                                                        )
                                                    "
                                                >
                                                    全選
                                                </a-button>
                                                <a-button
                                                    type="link"
                                                    size="small"
                                                    @click.stop="
                                                        toggleModuleAll(
                                                            root,
                                                            false
                                                        )
                                                    "
                                                >
                                                    清除
                                                </a-button>
                                            </span>
                                        </div>
                                    </template>
                                    <a-tree
                                        v-if="
                                            activeModuleKeys.includes(root.key)
                                        "
                                        checkable
                                        :key="`${root.key}-${moduleTreeVersion[root.key] || 0}`"
                                        class="perm-tree"
                                        :tree-data="
                                            treeNodesForModuleRoot(root)
                                        "
                                        :checked-keys="
                                            checkedKeysByModule[root.key] ||
                                            emptyTreeCheckState
                                        "
                                        :expanded-keys="
                                            expandedKeysByRoot[root.key] || []
                                        "
                                        :field-names="{
                                            title: 'title',
                                            key: 'key',
                                            children: 'children'
                                        }"
                                        :selectable="false"
                                        :show-line="false"
                                        :checkStrictly="true"
                                        block-node
                                        @check="
                                            (keys, info) =>
                                                onTreeCheckByRoot(
                                                    root,
                                                    keys,
                                                    info
                                                )
                                        "
                                        @expand="
                                            (keys) =>
                                                onTreeExpand(root.key, keys)
                                        "
                                    />
                                </a-collapse-panel>
                            </a-collapse>
                        </div>
                    </div>
                </a-form-item>
            </template>
        </EditModal>

        <!-- 新增角色 Modal -->
        <EditModal
            v-model:open="createRoleModalVisible"
            title="新增角色"
            :confirm-loading="createRoleLoading"
            @ok="submitCreateRole"
            @cancel="() => (createRoleModalVisible.value = false)"
        >
            <template #fields>
                <a-form-item label="角色名稱">
                    <a-input
                        v-model:value="newRoleName"
                        placeholder="請輸入角色名稱"
                    />
                </a-form-item>
            </template>
        </EditModal>

        <!-- 刪除角色 Modal -->
        <EditModal
            v-model:open="deleteRoleModalVisible"
            title="刪除角色"
            :confirm-loading="false"
            @ok="() => removeRole(deleteTarget)"
            @cancel="() => (deleteRoleModalVisible.value = false)"
        >
            <template #fields>
                <div>確認刪除角色「{{ deleteTarget?.name }}」？</div>
                <div
                    v-if="(deleteTarget?.users_count ?? 0) > 0"
                    class="text-red-500 mt-2"
                >
                    該角色已有綁定帳號，無法刪除
                </div>
            </template>
        </EditModal>
    </div>
</template>

<script setup>
// RoleList Component - 角色管理頁面
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import {
    showErrorMessage,
    handleValidationErrors
} from '@/utils/messageHelper';
import {
    getRoles,
    getRoleDetail,
    getPermissions,
    getPermissionList,
    updateRole as updateRoleApi,
    createRole as createRoleApi,
    deleteRole as deleteRoleApi
} from '@/api/roles';
import { useAdminInfo } from '@/stores/useAdminInfo';
import DataTable from '@/components/common/DataTable.vue';
import DetailModal from '@/components/common/DetailModal.vue';
import EditModal from '@/components/common/EditModal.vue';
import {
    buildPermissionTreeFromApi,
    treeNodesForModuleRoot,
    buildPermissionKeyToCodeMap,
    expandOwnedCodesToTreeKeys,
    keysToPermissionCodes,
    resolveModuleCheckedKeys,
    applyToggleFromCheckInfo,
    normalizeModuleCheckedKeys,
    normalizeSelectionWithGroupParents,
    computeTreeCheckState,
    preparePermissionSelectionForSubmit,
    flattenTreeKeys
} from '@/utils/permissionTree';

const emptyTreeCheckState = { checked: [], halfChecked: [] };

// 資料狀態
const roles = ref([]);
const loading = ref(false);
const searchText = ref('');
const router = useRouter();
const adminStore = useAdminInfo();

// 按鈕權限定義（依權限代碼控制顯示）
const canCreate = computed(() => adminStore.hasPermissionCode('990101')); // 新增角色
const canEdit = computed(() => adminStore.hasPermissionCode('990102')); // 修改角色
const canDelete = computed(() => adminStore.hasPermissionCode('990103')); // 刪除角色

// 計算屬性：角色詳情資訊
const roleDetailInfo = computed(() => {
    if (!roleDetail.value) return [];

    return [
        { label: '角色名稱', value: roleDetail.value.name },
        { label: '建立時間', value: roleDetail.value.created_at },
        { label: '更新時間', value: roleDetail.value.updated_at }
    ];
});

// 計算屬性：當前角色資訊
const currentRoleInfo = computed(() => {
    if (!currentRole.value) return [];

    return [{ label: '角色名稱', value: currentRole.value.name }];
});
const allPermissions = ref([]);
/** 資料庫 permissions.name（manager-api），送出時只允許此集合 */
const validPermissionCodes = ref(new Set());
// Tree 狀態（編輯權限）
const expandedAll = ref(true);
const expandedKeysByRoot = ref({});
const activeModuleKeys = ref([]);
const permSearch = ref('');
const selectedPermissionIds = ref([]);
const syncingSelection = ref(false);
const moduleTreeVersion = ref({});

const permissionTreeData = computed(() =>
    buildPermissionTreeFromApi(allPermissions.value)
);

// 供送出時補齊祖先節點代碼：只在提交時計算，不影響「畫面選中集合」
const permissionKeyToCode = computed(() =>
    buildPermissionKeyToCodeMap(permissionTreeData.value)
);

const flattenKeys = (arr = []) => flattenTreeKeys(arr);

const allModuleKeys = computed(() =>
    permissionTreeData.value.map((r) => r.key)
);

const totalPermissionKeys = computed(() =>
    flattenKeys(permissionTreeData.value)
);

const moduleFlatKeysByRoot = computed(() => {
    const map = new Map();
    permissionTreeData.value.forEach((root) => {
        map.set(root.key, flattenKeys([root]));
    });
    return map;
});

const moduleKeySets = computed(() => {
    const map = new Map();
    moduleFlatKeysByRoot.value.forEach((keys, rootKey) => {
        map.set(rootKey, new Set(keys));
    });
    return map;
});

const checkedKeysByModule = computed(() => {
    const selected = new Set(selectedPermissionIds.value);
    const out = {};
    permissionTreeData.value.forEach((root) => {
        const moduleKeys = moduleKeySets.value.get(root.key);
        if (!moduleKeys) return;
        const moduleSelected = [...moduleKeys].filter((k) => selected.has(k));
        out[root.key] = computeTreeCheckState(root, moduleSelected, moduleKeys);
    });
    return out;
});

const moduleSelectionStats = computed(() => {
    const selected = new Set(selectedPermissionIds.value);
    const stats = {};
    moduleFlatKeysByRoot.value.forEach((keys, rootKey) => {
        const total = keys.length;
        const selectedCount = keys.filter((k) => selected.has(k)).length;
        stats[rootKey] = {
            selected: selectedCount,
            total,
            complete: total > 0 && selectedCount === total
        };
    });
    return stats;
});

const selectedPermissionCount = computed(() => {
    const allowed = new Set(totalPermissionKeys.value);
    return selectedPermissionIds.value.filter((k) => allowed.has(k)).length;
});

const totalPermissionCount = computed(() => totalPermissionKeys.value.length);

const selectionSetsEqual = (a, b) => {
    if (a.length !== b.length) return false;
    const setA = new Set(a);
    return b.every((k) => setA.has(k));
};

const applySelection = (next, bumpRootKey = null) => {
    if (selectionSetsEqual(next, selectedPermissionIds.value)) {
        return;
    }
    syncingSelection.value = true;
    selectedPermissionIds.value = next;
    if (bumpRootKey) {
        moduleTreeVersion.value = {
            ...moduleTreeVersion.value,
            [bumpRootKey]: (moduleTreeVersion.value[bumpRootKey] || 0) + 1
        };
    }
    nextTick(() => {
        syncingSelection.value = false;
    });
};

const onTreeExpand = (rootKey, keys) => {
    expandedKeysByRoot.value = {
        ...expandedKeysByRoot.value,
        [rootKey]: keys
    };
};

const toggleModuleAll = (root, select) => {
    const moduleKeys = moduleFlatKeysByRoot.value.get(root.key) || [];
    const current = new Set(selectedPermissionIds.value);
    if (select) {
        moduleKeys.forEach((k) => current.add(k));
        normalizeModuleCheckedKeys(
            root,
            [...current],
            moduleKeySets.value.get(root.key)
        ).forEach((k) => current.add(k));
    } else {
        moduleKeys.forEach((k) => current.delete(k));
    }
    applySelection(Array.from(current), root.key);
};

const syncExpandedState = () => {
    if (expandedAll.value) {
        activeModuleKeys.value = [...allModuleKeys.value];
        const byRoot = {};
        permissionTreeData.value.forEach((root) => {
            byRoot[root.key] = moduleFlatKeysByRoot.value.get(root.key) || [];
        });
        expandedKeysByRoot.value = byRoot;
        return;
    }
    activeModuleKeys.value = [];
    expandedKeysByRoot.value = {};
};

const selectAllPermissions = () => {
    applySelection(
        normalizeSelectionWithGroupParents(
            permissionTreeData.value,
            totalPermissionKeys.value
        )
    );
};

const clearAllPermissions = () => {
    applySelection([]);
};

// 預設全展開（模組手風琴 + 樹節點）
watch(
    () => permissionTreeData.value,
    () => {
        syncExpandedState();
    },
    { immediate: true }
);

watch(
    () => expandedAll.value,
    () => {
        syncExpandedState();
    }
);

watch(permSearch, () => {
    const q = permSearch.value.trim();
    if (!q) {
        syncExpandedState();
        return;
    }
    activeModuleKeys.value = filteredTreeData.value.map((r) => r.key);
    const byRoot = {};
    filteredTreeData.value.forEach((root) => {
        byRoot[root.key] = flattenKeys([root]);
    });
    expandedKeysByRoot.value = byRoot;
});

// checkStrictly：以 info.node 連動子孫，並補強差異比對與父節點完整性
const onTreeCheckByRoot = (root, keys, info) => {
    if (syncingSelection.value) return;

    const checked = Array.isArray(keys) ? keys : keys?.checked || [];
    const moduleKeys = moduleKeySets.value.get(root.key);
    if (!moduleKeys) return;

    const selectedSet = new Set(selectedPermissionIds.value);
    const prevModule = [...moduleKeys].filter((k) => selectedSet.has(k));
    const fromInfo = applyToggleFromCheckInfo(
        root,
        prevModule,
        info,
        moduleKeys
    );
    let normalizedChecked =
        fromInfo ??
        resolveModuleCheckedKeys(root, checked, prevModule, moduleKeys);
    normalizedChecked = normalizeModuleCheckedKeys(
        root,
        normalizedChecked,
        moduleKeys
    );

    if (selectionSetsEqual(normalizedChecked, prevModule)) {
        return;
    }

    const current = new Set(selectedPermissionIds.value);
    moduleKeys.forEach((k) => current.delete(k));
    normalizedChecked.forEach((k) => current.add(k));

    applySelection(Array.from(current), root.key);
};

// 搜尋過濾（名稱或代碼包含）
const filteredTreeData = computed(() => {
    const q = permSearch.value.trim().toLowerCase();
    if (!q) return permissionTreeData.value;

    const filter = (nodes = []) =>
        nodes
            .map((n) => {
                const title = String(n.title || '').toLowerCase();
                const hit =
                    title.includes(q) || String(n.code || '').includes(q);
                const children = n.children ? filter(n.children) : undefined;
                if (hit || (children && children.length)) {
                    return { ...n, children };
                }
                return null;
            })
            .filter(Boolean);

    return filter(permissionTreeData.value);
});

// 分頁設定
const pagination = reactive({
    current: 1,
    pageSize: 10,
    total: 0
});

// Modal 狀態
const permissionModalVisible = ref(false);
const detailModalVisible = ref(false);
const currentRole = ref(null);
const currentRolePermissions = ref([]);
const roleDetail = ref(null);
// 編輯權限相關狀態
const editPermissionsModalVisible = ref(false);
const editPermissionsLoading = ref(false);
const editRoleName = ref('');
// 新增角色
const createRoleModalVisible = ref(false);
const createRoleLoading = ref(false);
const newRoleName = ref('');
// 刪除角色
const deleteRoleModalVisible = ref(false);
const deleteTarget = ref(null);

// 表格欄位配置
const columns = [
    {
        title: '角色名稱',
        key: 'name',
        ellipsis: true,
        width: 200
    },
    {
        title: '綁定帳號數量',
        key: 'users_count',
        align: 'center',
        ellipsis: true,
        width: 120
    },
    {
        title: '權限',
        key: 'permissions',
        ellipsis: true
    },
    {
        title: '更新時間',
        dataIndex: 'updated_at',
        key: 'updated_at',
        ellipsis: true
    },
    {
        title: '操作',
        key: 'action',
        align: 'center'
    }
];

// 載入角色列表
const loadRoles = async () => {
    try {
        loading.value = true;

        const response = await getRoles({
            page: pagination.current,
            per_page: pagination.pageSize,
            name: searchText.value // 加入搜尋參數：以 name 作為查詢鍵
        });

        if (response.result) {
            // API 直接返回數組，不是分頁格式
            if (Array.isArray(response.data)) {
                roles.value = response.data;
                pagination.total = response.data.length;
                // 如果需要前端分頁，可以在這裡處理
            } else if (response.data && response.data.data) {
                // 如果是分頁格式
                roles.value = response.data.data || [];
                const pag = response.data?.pagination || {};
                pagination.total = pag.total ?? response.data?.total ?? 0;
                pagination.current = pag.current_page ?? pagination.current;
                pagination.pageSize = pag.per_page ?? pagination.pageSize;
            }
        }
    } catch (error) {
        console.error('載入角色列表失敗:', error);
        showErrorMessage('載入角色列表失敗');
    } finally {
        loading.value = false;
    }
};

// 載入所有權限（使用 /permissions 回傳的階層物件）
const loadAllPermissions = async () => {
    try {
        const [hierarchyRes, listRes] = await Promise.all([
            getPermissions(),
            getPermissionList('manager-api')
        ]);
        if (hierarchyRes.result) {
            allPermissions.value = hierarchyRes.data || {};
        }
        if (listRes.result) {
            validPermissionCodes.value = new Set(
                (listRes.data || [])
                    .map((p) => String(p.code ?? p.name ?? ''))
                    .filter((c) => /^\d{6}$/.test(c))
            );
        }
    } catch (error) {
        console.error('載入權限列表失敗:', error);
    }
};

// 獲取展示用的權限（最多顯示3個）
const getDisplayPermissions = (permissions) => {
    if (!permissions || permissions.length === 0) return [];
    return permissions.slice(0, 3);
};

// 搜尋處理
const handleSearch = () => {
    pagination.current = 1;
    loadRoles();
};

// 重置篩選
const resetFilters = () => {
    searchText.value = '';
    pagination.current = 1;
    loadRoles();
};

// 表格變更處理
const handleTableChange = ({ pagination: paginationInfo }) => {
    pagination.current = paginationInfo.current;
    pagination.pageSize = paginationInfo.pageSize;
    loadRoles();
};

// 導向到管理員管理頁，並帶入角色做為篩選
const goToAdminList = (role) => {
    try {
        const roleId = role?.id ?? role?.role_id;
        // 使用 key: role，不再使用 role_id
        const query = roleId ? { role: roleId } : {};
        router.push({ name: 'AdminList', query });
    } catch (e) {}
};

// 新增角色流程
const openCreateRole = () => {
    newRoleName.value = '';
    createRoleModalVisible.value = true;
};

const submitCreateRole = async () => {
    if (!newRoleName.value || newRoleName.value.trim() === '') {
        message.warning('請輸入角色名稱');
        return;
    }
    try {
        createRoleLoading.value = true;
        const payload = { name: newRoleName.value.trim() };
        const res = await createRoleApi(payload);
        if (res.result) {
            message.success('已新增角色');
            createRoleModalVisible.value = false;
            await loadRoles();
        } else {
            // 處理驗證錯誤
            const errorMessage = handleValidationErrors(res, {
                name: '角色名稱'
            });
            showErrorMessage(errorMessage);
        }
    } catch (e) {
        console.error('新增角色失敗:', e);
        // 檢查 catch 中的錯誤是否包含 errors 物件
        const errorData = e?.response?.data || e?.data || e;
        if (errorData?.errors) {
            const errorMessage = handleValidationErrors(errorData, {
                name: '角色名稱'
            });
            showErrorMessage(errorMessage);
        } else {
            showErrorMessage('新增角色失敗');
        }
    } finally {
        createRoleLoading.value = false;
    }
};

// 刪除角色（僅當綁定帳號數量為 0 時啟用）
const removeRole = async (record) => {
    const count = record.users_count ?? 0;
    if (count > 0) {
        message.warning('該角色已有綁定帳號，無法刪除');
        return;
    }
    try {
        const res = await deleteRoleApi(record.id);
        if (res.result) {
            message.success('角色已刪除');
            await loadRoles();
            deleteRoleModalVisible.value = false;
            deleteTarget.value = null;
        } else {
            showErrorMessage(res.message || '刪除失敗');
        }
    } catch (e) {
        console.error('刪除角色失敗:', e);
        showErrorMessage('刪除角色失敗');
    } finally {
        // 保險：不論成功失敗都嘗試關閉彈窗（若 API 失敗會保留訊息）
        deleteRoleModalVisible.value = false;
    }
};

const openDeleteRole = (record) => {
    if ((record.users_count ?? 0) > 0) return;
    deleteTarget.value = record;
    deleteRoleModalVisible.value = true;
};

// 檢視權限（改為呼叫細節 API 取得巢狀 permissions）
const viewPermissions = async (record) => {
    try {
        const response = await getRoleDetail(record.id);
        if (response.result) {
            currentRole.value = response.data;
            currentRolePermissions.value = response.data?.permissions || [];
            permissionModalVisible.value = true;
        }
    } catch (error) {
        console.error('載入角色權限失敗:', error);
        showErrorMessage('載入角色權限失敗');
    }
};

// 開啟編輯權限 Modal
const openEditPermissions = async (record) => {
    try {
        currentRole.value = record;
        editRoleName.value = record.name || '';
        moduleTreeVersion.value = {};
        expandedAll.value = true;
        if (permissionTreeData.value.length === 0) {
            await loadAllPermissions();
        }
        const ownedCodes = (record.permissions || [])
            .map((p) => p.code ?? p.name)
            .filter((c) => c && /^\d{6}$/.test(String(c)));
        selectedPermissionIds.value = expandOwnedCodesToTreeKeys(
            ownedCodes,
            permissionTreeData.value
        );
        editPermissionsModalVisible.value = true;
        nextTick(() => syncExpandedState());
    } catch (error) {
        console.error('開啟編輯權限失敗:', error);
        showErrorMessage('開啟編輯權限失敗');
    }
};

// 送出編輯權限
const submitEditPermissions = async () => {
    if (!currentRole.value) return;
    try {
        editPermissionsLoading.value = true;
        const payload = {
            name: (editRoleName.value || '').trim(),
            // 送出清單：畫面選中集合 + 其所有祖先（確保父層在子層有選時始終包含）
            // 只送實際勾選對應的授權碼；父層模組碼由後端 processPermissions 依規則補齊（冰火 171xxx 不補 170000）
            permission_codes: keysToPermissionCodes(
                preparePermissionSelectionForSubmit(
                    permissionTreeData.value,
                    selectedPermissionIds.value
                ),
                permissionKeyToCode.value,
                { allowedCodes: validPermissionCodes.value }
            )
        };
        const response = await updateRoleApi(currentRole.value.id, payload);
        if (response.result) {
            message.success('權限已更新');
            editPermissionsModalVisible.value = false;
            // 更新列表資料
            await loadRoles();
        } else {
            // 處理驗證錯誤
            const errorMessage = handleValidationErrors(response, {
                name: '角色名稱'
            });
            showErrorMessage(errorMessage);
        }
    } catch (error) {
        console.error('更新角色權限失敗:', error);
        // 檢查 catch 中的錯誤是否包含 errors 物件
        const errorData = error?.response?.data || error?.data || error;
        if (errorData?.errors) {
            const errorMessage = handleValidationErrors(errorData, {
                name: '角色名稱'
            });
            showErrorMessage(errorMessage);
        } else {
            showErrorMessage('更新角色權限失敗');
        }
    } finally {
        editPermissionsLoading.value = false;
    }
};

// 取消編輯權限
const cancelEditPermissions = () => {
    editPermissionsModalVisible.value = false;
};

// 初始化
onMounted(() => {
    loadRoles();
    loadAllPermissions();
});
</script>

<style scoped>
/* 權限編輯：手風琴模組 + 可捲動區 */
.perm-panel {
    border: 1px solid rgba(42, 74, 122, 0.25);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.92);
    overflow: hidden;
}

.perm-tree-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    border-bottom: 1px solid rgba(42, 74, 122, 0.15);
    background: rgba(248, 245, 255, 0.65);
}

.perm-search {
    width: 240px;
}

.perm-summary {
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 600;
    color: #6a4a8a;
    white-space: nowrap;
}

.perm-scroll {
    max-height: min(58vh, 520px);
    overflow-y: auto;
    padding: 8px 10px 10px;
}

.perm-collapse :deep(.ant-collapse-item) {
    border: 1px solid rgba(42, 74, 122, 0.18) !important;
    border-radius: 10px !important;
    margin-bottom: 8px;
    overflow: hidden;
    background: #fff;
}

.perm-collapse :deep(.ant-collapse-item:last-child) {
    margin-bottom: 0;
}

.perm-collapse :deep(.ant-collapse-header) {
    padding: 8px 12px !important;
    align-items: center !important;
    background: rgba(248, 245, 255, 0.45);
}

.perm-collapse :deep(.ant-collapse-content-box) {
    padding: 4px 8px 8px !important;
}

.perm-module-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding-right: 4px;
}

.perm-module-title {
    font-weight: 600;
    color: #2a2a3a;
}

.perm-module-meta {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
}

.perm-module-count {
    min-width: 44px;
    padding: 0 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 22px;
    text-align: center;
    color: #6a6a7a;
    background: rgba(42, 74, 122, 0.12);
}

.perm-module-count.is-complete {
    color: #fff;
    background: #2a4a7a;
}

.perm-tree :deep(.ant-tree-treenode) {
    padding: 1px 0;
}

.perm-tree :deep(.ant-tree-indent-unit) {
    width: 14px;
}

.perm-tree :deep(.ant-tree-node-content-wrapper) {
    min-height: 26px;
    line-height: 26px;
    border-radius: 6px;
}

.perm-tree :deep(.ant-tree-switcher) {
    width: 20px;
}

/* 權限樹 checkbox：未勾也要清楚可見 */
.perm-tree :deep(.ant-tree-checkbox-inner) {
    width: 16px !important;
    height: 16px !important;
    background: #ffffff !important;
    border: 2px solid #245a8c !important;
    border-radius: 3px !important;
}

.perm-tree :deep(.ant-tree-checkbox:hover .ant-tree-checkbox-inner) {
    border-color: #1a4268 !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.3) !important;
}

.perm-tree :deep(.ant-tree-checkbox-checked .ant-tree-checkbox-inner) {
    background-color: #245a8c !important;
    border-color: #1a4268 !important;
}

.perm-tree :deep(.ant-tree-checkbox-indeterminate .ant-tree-checkbox-inner) {
    background-color: #d4b3ff !important;
    border-color: #245a8c !important;
}

.perm-tree :deep(.ant-tree-checkbox-checked .ant-tree-checkbox-inner::after),
.perm-tree
    :deep(.ant-tree-checkbox-indeterminate .ant-tree-checkbox-inner::after) {
    border-color: #ffffff !important;
    border-width: 0 2px 2px 0 !important;
    opacity: 1 !important;
}

/* 被選節點文字更明顯 */
:deep(.ant-tree-checkbox-checked + .ant-tree-node-content-wrapper) {
    color: #2a2a3a !important;
    font-weight: 700 !important;
    text-shadow: 0 0 8px rgba(42, 74, 122, 0.25);
}

.page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header {
    margin-bottom: 24px;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

.content {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 32px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    position: relative;
    z-index: 1;
}

.search-box {
    flex: 1;
    max-width: 400px;
}

.role-table {
    position: relative;
    z-index: 1;
}

.role-name {
    font-weight: 500;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.permissions-cell {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
}

.more-permissions {
    color: #6a6a7a;
    font-size: 12px;
    margin-left: 4px;
    font-family: 'Courier New', monospace;
}

.clickable {
    cursor: pointer;
    text-decoration: underline;
    color: #3b6ea5;
}

.role-info {
    margin-bottom: 16px;
}

.role-info h3 {
    margin: 0 0 8px 0;
    font-size: 18px;
    color: #64748b;
    font-family: 'Courier New', monospace;
    font-weight: 600;
}

.role-info p {
    margin: 0;
    color: #0e7490;
    font-family: 'Courier New', monospace;
}

.permissions-grid {
    max-height: 400px;
    overflow-y: auto;
}

.permission-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 8px;
}

.permission-name {
    flex: 1;
    color: #2a2a3a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

.detail-content {
    padding: 16px 0;
}

.permission-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

/* DataTable vaporwave theme integration */
:deep(.ant-card) {
    background: transparent;
    border: none;
}

:deep(.ant-card-body) {
    padding: 0;
}

:deep(.ant-table) {
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 12px;
    overflow: hidden;
}

:deep(.ant-table-thead > tr > th) {
    background: rgba(255, 255, 255, 0.9);
    color: #64748b;
    font-weight: 600;
    font-family: 'Courier New', monospace;
    border-bottom: 1px solid rgba(42, 74, 122, 0.3);
}

:deep(.ant-table-tbody > tr > td) {
    background: rgba(255, 255, 255, 0.6);
    color: #2a2a3a;
    border-bottom: 1px solid rgba(42, 74, 122, 0.2);
    font-family: 'Courier New', monospace;
}

:deep(.ant-table-tbody > tr:hover > td) {
    background: rgba(255, 255, 255, 0.6);
    color: #2a2a3a;
}

:deep(.ant-btn-primary) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5);
    border: none;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: 600;
}

:deep(.ant-btn-primary:hover) {
    background: linear-gradient(45deg, #3b6ea5, #2a4a7a);
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.5);
}

:deep(.ant-btn) {
    font-family: 'Courier New', monospace;
    border-radius: 8px;
}

:deep(.ant-btn-default) {
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(200, 200, 210, 0.4);
    color: #2a2a3a;
}

:deep(.ant-btn-default:hover) {
    background: rgba(42, 74, 122, 0.1);
    border-color: rgba(200, 200, 210, 0.4);
    color: #2a2a3a;
}

:deep(.ant-tag) {
    border-radius: 12px;
    padding: 2px 12px;
    font-family: 'Courier New', monospace;
    font-weight: 500;
}

:deep(.ant-tag-blue) {
    background: rgba(138, 175, 255, 0.2);
    border-color: #3b6ea5;
    color: #3b6ea5;
}

:deep(.ant-tag-purple) {
    background: rgba(42, 74, 122, 0.2);
    border-color: #2a4a7a;
    color: #2a4a7a;
}

:deep(.ant-tag-green) {
    background: rgba(208, 255, 183, 0.2);
    border-color: #d0ffb7;
    color: #d0ffb7;
}

:deep(.ant-modal-content) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 16px;
}

:deep(.ant-modal-content::before) {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
}

:deep(.ant-modal-header) {
    background: transparent;
    border-bottom: 1px solid rgba(42, 74, 122, 0.3);
}

:deep(.ant-modal-title) {
    color: #64748b;
    font-family: 'Courier New', monospace;
    font-weight: 600;
}

:deep(.ant-modal-body) {
    color: #2a2a3a;
}

:deep(.ant-divider) {
    border-color: rgba(42, 74, 122, 0.3);
}

:deep(.ant-descriptions-item-label) {
    background: rgba(255, 255, 255, 0.8);
    color: #0e7490;
    font-family: 'Courier New', monospace;
}

:deep(.ant-descriptions-item-content) {
    background: rgba(255, 255, 255, 0.6);
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

:deep(.ant-pagination-total-text) {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

/* Filter 輸入框統一樣式 */
.filters :deep(.filter-input) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.1) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    padding: 0 12px !important;
    position: relative;
    z-index: 1;
    width: 300px;
    transition: all 0.3s ease !important;
}

.filters :deep(.filter-input::placeholder) {
    color: rgba(42, 42, 58, 0.5) !important;
    opacity: 1 !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
}

.filters :deep(.filter-input:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.2) !important;
}

.filters :deep(.filter-input:focus) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
}

.action-buttons :deep(.ant-space-item) {
    margin-right: 4px !important;
}
</style>
